import { useState } from 'react'
import { Copy, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type { ApiClientResponse } from '@/hooks/useApiClient'

function statusColor(status: number): string {
  if (status < 300) return 'text-green-600'
  if (status < 400) return 'text-blue-600'
  if (status < 500) return 'text-yellow-600'
  return 'text-red-600'
}

function tryFormatJson(text: string): { formatted: string; isJson: boolean } {
  try {
    const parsed = JSON.parse(text)
    return { formatted: JSON.stringify(parsed, null, 2), isJson: true }
  } catch {
    return { formatted: text, isJson: false }
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const { t } = useTranslation()
  const handleCopy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={handleCopy} title={t('common.copy')}>
      {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  )
}

interface Props {
  response: ApiClientResponse
}

export function ResponseViewer({ response }: Props) {
  const { formatted, isJson } = tryFormatJson(response.body)

  const filteredHeaders = Object.entries(response.headers).filter(
    ([k]) => !['content-encoding', 'transfer-encoding'].includes(k.toLowerCase()),
  )

  return (
    <div className="space-y-4">
      {/* Status line */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className={cn('text-2xl font-bold tabular-nums', statusColor(response.status))}>
          {response.status}
        </span>
        <span className="text-sm text-muted-foreground">{response.statusText}</span>
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {response.latencyMs} ms
        </span>
      </div>

      {/* Response body */}
      {response.body !== '' && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Body {isJson && <span className="normal-case font-normal">(JSON)</span>}
            </p>
            <CopyButton text={formatted} />
          </div>
          <pre className="rounded-md border bg-muted/40 p-3 text-xs font-mono overflow-auto max-h-80 whitespace-pre-wrap break-all">
            {formatted}
          </pre>
        </div>
      )}

      {/* Response headers */}
      {filteredHeaders.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Headers</p>
          <div className="rounded-md border divide-y text-xs font-mono">
            {filteredHeaders.map(([k, v]) => (
              <div key={k} className="flex gap-2 px-3 py-1.5">
                <span className="text-muted-foreground shrink-0">{k}:</span>
                <span className="break-all">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
